// TemplateSendModal.tsx - Modal para enviar templates fora da janela de 24h
import React, { useEffect, useRef, useState } from 'react';
import {
    X,
    Send,
    Loader2,
    CheckCircle2,
    ChevronDown
} from 'lucide-react';
import {
    getTemplatesForChat,
    sendTemplateDirect,
    parseApiError,
    TemplateForChat,
    TemplateSendResponse,
    type ParsedApiError
} from '../services/api.ts';
import WhatsAppIdentityErrorAlert from './WhatsAppIdentityErrorAlert.tsx';
import { useTheme } from '../contexts/ThemeContext.tsx';

interface TemplateSendModalProps {
    isOpen: boolean;
    onClose: () => void;
    clinicId: number;
    contactId?: number;
    contactPhone: string;
    contactName: string;
    onSuccess?: (response: TemplateSendResponse) => void;
    onSelectExistingContact?: (identityError: ParsedApiError) => void;
}

export function TemplateSendModal({
    isOpen,
    onClose,
    clinicId,
    contactId,
    contactPhone,
    contactName,
    onSuccess,
    onSelectExistingContact
}: TemplateSendModalProps) {
    const { isDark } = useTheme();
    const dialogRef = useRef<HTMLDivElement>(null);
    const [templates, setTemplates] = useState<TemplateForChat[]>([]);
    const [selectedTemplate, setSelectedTemplate] = useState<TemplateForChat | null>(null);
    const [loading, setLoading] = useState(true);
    const [sending, setSending] = useState(false);
    const [error, setError] = useState<ParsedApiError | null>(null);
    const [success, setSuccess] = useState<TemplateSendResponse | null>(null);
    const modalActionsRef = useRef({ onClose, sending });
    modalActionsRef.current = { onClose, sending };

    useEffect(() => {
        if (isOpen && clinicId) {
            loadData();
        }
    }, [isOpen, clinicId]);

    useEffect(() => {
        if (!isOpen) return;
        const previousFocus = document.activeElement as HTMLElement | null;
        dialogRef.current?.focus();
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape' && !modalActionsRef.current.sending) modalActionsRef.current.onClose();
            if (event.key !== 'Tab') return;
            const controls = dialogRef.current?.querySelectorAll<HTMLElement>('button:not(:disabled), select:not(:disabled), [tabindex="0"]');
            if (!controls?.length) {
                event.preventDefault();
                dialogRef.current?.focus();
                return;
            }
            const first = controls[0];
            const last = controls[controls.length - 1];
            if (event.shiftKey && (document.activeElement === first || document.activeElement === dialogRef.current)) {
                event.preventDefault();
                last.focus();
            } else if (!event.shiftKey && (document.activeElement === last || document.activeElement === dialogRef.current)) {
                event.preventDefault();
                first.focus();
            }
        };
        document.addEventListener('keydown', handleKeyDown);
        return () => {
            document.removeEventListener('keydown', handleKeyDown);
            previousFocus?.focus();
        };
    }, [isOpen]);

    const loadData = async () => {
        setLoading(true);
        setError(null);
        setSuccess(null);
        setSelectedTemplate(null);

        try {
            const templatesData = await getTemplatesForChat(clinicId);
            setTemplates(templatesData);
        } catch (err) {
            setTemplates([]);
            setError({ message: 'Erro ao carregar templates.' });
            console.error('Error loading templates:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleSend = async () => {
        if (!selectedTemplate || sending) return;

        setSending(true);
        setError(null);

        try {
            const response = await sendTemplateDirect({
                clinic_id: clinicId,
                phone: contactPhone,
                contact_id: contactId,
                template_name: selectedTemplate.name
            });

            if (response.success) {
                setSuccess(response);
                onSuccess?.(response);
                setTimeout(() => onClose(), 1500);
            } else {
                setError({ message: response.error || 'Erro ao enviar template' });
            }
        } catch (err) {
            setError(parseApiError(err, 'Erro ao enviar template.'));
            console.error('Error sending template:', err);
        } finally {
            setSending(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4 pt-[max(1rem,env(safe-area-inset-top))] pb-[max(1rem,env(safe-area-inset-bottom))]">
            <div
                ref={dialogRef}
                role="dialog"
                aria-modal="true"
                aria-label="Enviar template"
                tabIndex={-1}
                style={{ maxHeight: 'calc(100dvh - max(1rem, env(safe-area-inset-top)) - max(1rem, env(safe-area-inset-bottom)))' }}
                className={`rounded-2xl shadow-2xl w-full max-w-[560px] flex flex-col overflow-hidden outline-none border ${isDark ? 'bg-sminy-dark-elevated border-sminy-dark-border text-sminy-dark-text-primary' : 'bg-white border-sminy-border text-sminy-text-primary'}`}
            >
                {/* Header Compacto */}
                <div className="px-6 pt-6 flex items-center justify-between gap-3 shrink-0">
                    <h2 className="text-xl font-semibold">Enviar template</h2>
                    <button onClick={onClose} disabled={sending} aria-label="Fechar" className={`min-w-11 min-h-11 flex items-center justify-center rounded-xl focus-visible:ring-2 focus-visible:ring-sminy-primary disabled:opacity-50 ${isDark ? 'hover:bg-sminy-dark-surface-subtle' : 'hover:bg-sminy-blue-soft'}`}>
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {/* Conteúdo */}
                <div className="p-6 space-y-6 overflow-y-auto min-h-0">
                    <p className={`text-xs break-words ${isDark ? 'text-sminy-dark-text-secondary' : 'text-sminy-text-secondary'}`}>Para: {contactName || contactPhone}</p>
                    {loading ? (
                        <div role="status" aria-label="Carregando templates" className="flex items-center justify-center gap-2 py-8">
                            <Loader2 className="w-6 h-6 animate-spin text-sminy-primary" />
                            <span className="text-sm">Carregando templates...</span>
                        </div>
                    ) : error && !success ? (
                        <WhatsAppIdentityErrorAlert
                            error={error}
                            onOpenExisting={
                                error.existingContactId && onSelectExistingContact
                                    ? () => onSelectExistingContact(error)
                                    : undefined
                            }
                            existingActionLabel="Revisar cadastros duplicados"
                        />
                    ) : success ? (
                        <div className="flex flex-col items-center py-6 gap-3">
                            <CheckCircle2 className="w-12 h-12 text-green-500" />
                            <p className={`font-semibold ${isDark ? 'text-green-400' : 'text-green-700'}`}>Template enviado!</p>
                        </div>
                    ) : templates.length === 0 ? (
                        <div className={`text-center py-8 ${isDark ? 'text-sminy-dark-muted' : 'text-sminy-muted'}`}>
                            <p>Nenhum template aprovado.</p>
                        </div>
                    ) : (
                        <>
                            {/* Seletor */}
                            <div>
                                <label className="block text-xs font-medium mb-3">Template aprovado</label>
                                <div className="relative">
                                <select
                                    aria-label="Template aprovado"
                                    disabled={sending}
                                    value={selectedTemplate?.id || ''}
                                    onChange={(e) => {
                                        const t = templates.find(t => t.id === Number(e.target.value));
                                        setSelectedTemplate(t || null);
                                    }}
                                    className={`w-full min-h-11 px-3 py-2.5 pr-10 border rounded-xl appearance-none text-sm focus:ring-2 focus:ring-sminy-primary focus:border-sminy-primary disabled:opacity-50 ${isDark ? 'bg-sminy-dark-surface border-sminy-dark-border text-sminy-dark-text-primary' : 'bg-white border-sminy-border text-sminy-text-primary'}`}
                                >
                                    <option value="">Selecione um template...</option>
                                    {templates.map(t => (
                                        <option key={t.id} value={t.id}>
                                            {t.name} · {t.category}
                                        </option>
                                    ))}
                                </select>
                                <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
                                </div>
                            </div>

                            {/* Preview */}
                            {selectedTemplate && (
                                <div className={`p-4 rounded-xl border text-sm ${isDark ? 'bg-sminy-dark-surface-subtle border-sminy-dark-border' : 'bg-sminy-blue-soft border-sminy-border'}`}>
                                    <div className="mb-3">
                                        <span className="text-[10px] font-medium uppercase">
                                            {selectedTemplate.category}
                                        </span>
                                    </div>
                                    <p className={`whitespace-pre-wrap break-words ${isDark ? 'text-sminy-dark-text-secondary' : 'text-sminy-text-secondary'}`}>
                                        {selectedTemplate.body_text || 'Prévia não disponível'}
                                    </p>
                                </div>
                            )}
                        </>
                    )}
                    {!success && (
                        <p className={`p-4 rounded-xl border text-xs ${isDark ? 'bg-sminy-dark-surface-subtle border-sminy-dark-border text-sminy-dark-text-secondary' : 'bg-sminy-blue-soft border-sminy-border text-sminy-text-secondary'}`}>
                            As tarifas do WhatsApp Oficial são cobradas diretamente pela Meta.
                        </p>
                    )}
                </div>

                {/* Footer */}
                {!success && templates.length > 0 && !loading && (
                    <div className="px-6 pb-6 flex gap-3 shrink-0">
                        <button
                            onClick={onClose}
                            disabled={sending}
                            className={`flex-1 min-h-11 px-4 py-2.5 border rounded-xl text-sm font-medium focus-visible:ring-2 focus-visible:ring-sminy-primary disabled:opacity-50 ${isDark ? 'bg-sminy-dark-surface border-sminy-dark-border hover:bg-sminy-dark-surface-subtle' : 'border-sminy-border hover:bg-sminy-blue-soft'}`}
                        >
                            Cancelar
                        </button>
                        <button
                            onClick={handleSend}
                            disabled={!selectedTemplate || sending || !!error}
                            className="flex-1 min-h-11 flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium bg-sminy-primary text-white hover:bg-sminy-primary-hover active:bg-sminy-primary-pressed focus-visible:ring-2 focus-visible:ring-sminy-primary disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                            {sending ? 'Enviando...' : 'Enviar'}
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
}

export default TemplateSendModal;
